import React, { useMemo, CSSProperties } from 'react';
import cn from 'classnames';
import { Button } from '@heroui/react';
import { getResponsiveStyle } from '@/shared/lib/utils/getResponsiveStyle';
import { SvgType, ResponsiveSize } from '@/shared/types/common';
import { Svg } from '@/shared/ui/Svg';

export type AppButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
  fullWidth?: boolean;
  type?: string;
  color?: 'yellow' | 'black' | 'white' | 'red' | 'grey';
  children: React.ReactNode;
  onPress?: () => void;
  height?: ResponsiveSize;
  width?: ResponsiveSize;
  startIcon?: SvgType;
  endIcon?: SvgType;
  isLoading?: boolean;
  disabled?: boolean;
};

export const AppButton: React.FC<AppButtonProps> = (props) => {
  const {
    color,
    className,
    fullWidth,
    type = 'button',
    children,
    onPress,
    height,
    width,
    startIcon: StartIcon,
    endIcon: EndIcon,
    isLoading = false,
    disabled = false,
  } = props;

  const style = useMemo(() => {
    let style: CSSProperties = {};
    if (width) {
      style = getResponsiveStyle(width, 'width', style);
    }
    if (height) {
      style = getResponsiveStyle(height, 'height', style);
    }
    return style;
  }, [width, height]);

  const customClassName = useMemo(
    () =>
      cn(
        'transition-all duration-300 ease-in-out',
        'flex items-center justify-center cursor-pointer gap-2',
        'min-w-[140px] h-11 px-4 rounded-[8px]',
        'text-base font-medium',
        'border',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        {
          'w-full min-w-auto': fullWidth,
          'bg-yellow-primary text-black border-yellow-primary': color === 'yellow',
          'bg-[#211D55] text-white border-black': color === 'black',
          'bg-grey-brand text-black border-0': color === 'grey',
          'bg-white text-black border-black transition-duration-500': color === 'white',
          'hover:bg-[#211D55] hover:text-white': color === 'white' && !disabled && !isLoading,
          'bg-red-tertiary text-white border-red-tertiary': color === 'red',
        },
      ),
    [color, fullWidth, disabled, isLoading],
  );
  return (
    <Button
      className={cn(customClassName, className)}
      onPress={onPress}
      style={style}
      type={type}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        'Loading...'
      ) : (
        <>
          {StartIcon && <Svg Icon={StartIcon} />}
          {children}
          {EndIcon && <Svg Icon={EndIcon} />}
        </>
      )}
    </Button>
  );
};
